import { calcFormacaoPreco, calcImpostoAberto, calcMargemPorPreco, calcPrecoPorAno } from "../utils/calculoReforma";
import { DEFAULTS_BY_TIPO, SEGMENT_DEFS } from "../utils/formacaoPrecoDefs";
import { formatCurrency } from "../../creditos/dashboardData";
import { IOS } from "../../activity-logs/labels";

/**
 * Dados da aba Formação de preço. No modo simplificado, o cálculo é local, com os 3
 * segmentos fixos. No avançado, o resultado vem do backend, com o mesmo formato, então
 * as duas formas alimentam a mesma tela.
 */

export const TIPO_LABEL = {
  servico: "Serviço: ISS → CBS/IBS (mão de obra não credita)",
  normal: "Normal: ICMS/PIS-COFINS com crédito integral",
  "ST-monofasico": "Substituição Tributária monofásica",
  "base-reduzida": "Base reduzida / crédito presumido",
};

const fraction = (v) => (Number(v) || 0) / 100;

export const toCalcInputs = (seg) => ({
  custo: Number(seg.custo) || 0,
  pctFrete: fraction(seg.pctFrete),
  pctMateriais: fraction(seg.pctMateriais),
  pctComissao: fraction(seg.pctComissao),
  pctOpex: fraction(seg.pctOpex),
  pctMargem: fraction(seg.pctMargem),
  // Só nos segmentos do Cadastro: a parcela da despesa com crédito que o servidor usa
  ...(seg.despesasElegiveis !== undefined ? { despesasElegiveis: seg.despesasElegiveis } : {}),
});

export const money = (v) => (v === null || v === undefined ? "—" : formatCurrency(v));
export const percentOf = (v, digits = 1) => (v === null || v === undefined ? "—" : `${(Math.abs(v) < 0.0005 ? 0 : v * 100).toLocaleString("pt-BR", { minimumFractionDigits: digits, maximumFractionDigits: digits })}%`);
export const deltaOf = (r) => (r?.pvAtual && r?.pvFuturo ? (r.pvFuturo / r.pvAtual - 1) * 100 : null);
export const formatDelta = (d, digits = 1) => (d === null ? "—" : `${d >= 0 ? "+" : "−"}${Math.abs(d).toLocaleString("pt-BR", { minimumFractionDigits: digits, maximumFractionDigits: digits })}%`);

const factor = (v) => (v === null || v === undefined ? "—" : v.toLocaleString("pt-BR", { minimumFractionDigits: 4, maximumFractionDigits: 4 }));

export const simpleSegments = (params, formacaoInputs) => SEGMENT_DEFS.map((def) => {
  const inputs = { ...def.defaults, ...(formacaoInputs[def.id] || {}) };
  return {
    id: def.id,
    titulo: def.titulo,
    subtitulo: def.subtitulo,
    critico: def.critico,
    notaAtual: def.notaAtual,
    notaFuturo: def.notaFuturo,
    inputs,
    r: calcFormacaoPreco(def.id, toCalcInputs(inputs), params),
  };
});

// Modo "tenho o preço": o mesmo cálculo, com a margem que sobra em cada regime naquele preço
export function resultAtPrice(segmentId, inputs, params, price) {
  const calcInputs = toCalcInputs(inputs);
  const { margemAtual, margemFutura } = calcMargemPorPreco(segmentId, calcInputs, params, price);
  if (margemAtual === null) return { r: {}, marginToday: null, marginFuture: null };
  const today = calcFormacaoPreco(segmentId, { ...calcInputs, pctMargem: margemAtual }, params);
  const future = calcFormacaoPreco(segmentId, { ...calcInputs, pctMargem: margemFutura }, params);
  return {
    marginToday: margemAtual * 100,
    marginFuture: margemFutura * 100,
    r: {
      custoTotal: today.custoTotal,
      pvAtual: today.pvAtual,
      pvFuturo: future.pvFuturo,
      markupDivisorAtual: today.markupDivisorAtual,
      markupDivisorFuturo: future.markupDivisorFuturo,
      markupMultAtual: today.markupMultAtual,
      markupMultFuturo: future.markupMultFuturo,
      cargaLiquidaAtual: today.cargaLiquidaAtual,
      cargaLiquidaFuturo: future.cargaLiquidaFuturo,
      comissaoAtual: today.comissaoAtual,
      comissaoFuturo: future.comissaoFuturo,
      opexAtual: today.opexAtual,
      opexFuturo: future.opexFuturo,
      margemAtual: today.margemAtual,
      margemFuturo: future.margemFuturo,
    },
  };
}

export const advancedDefaults = (segmento) => ({
  ...(DEFAULTS_BY_TIPO[segmento.tipoRegime] || DEFAULTS_BY_TIPO.normal),
  pctMargem: (segmento.margemAlvoPadrao || 0.1) * 100,
});

// As partes do preço fecham no preço de venda: custo + carga + comissão + despesas + margem
export const priceParts = (r, regime) => {
  const suffix = regime === "atual" ? "Atual" : "Futuro";
  const pv = r?.[`pv${suffix}`];
  if (!pv) return null;
  return {
    pv,
    cost: r.custoTotal || 0,
    tax: (r[`cargaLiquida${suffix}`] || 0) * pv,
    commission: r[`comissao${suffix}`] || 0,
    opex: r[`opex${suffix}`] || 0,
    margin: r[`margem${suffix}`] || 0,
  };
};

export const formationRows = (r = {}) => [
  { key: "cost", label: "Custo total (c/ frete)", today: money(r.custoTotal), future: money(r.custoTotal) },
  { key: "pv", label: "Preço de venda mínimo", today: money(r.pvAtual), future: money(r.pvFuturo), delta: deltaOf(r), hero: true },
  { key: "div", label: "Markup divisor (Custo/PV)", today: factor(r.markupDivisorAtual), future: factor(r.markupDivisorFuturo) },
  { key: "mult", label: "Markup multiplicador (PV/Custo)", today: r.markupMultAtual ? `${factor(r.markupMultAtual)}×` : "—", future: r.markupMultFuturo ? `${factor(r.markupMultFuturo)}×` : "—" },
  { key: "tax", label: "Carga tributária líquida (% PV)", today: percentOf(r.cargaLiquidaAtual), future: percentOf(r.cargaLiquidaFuturo) },
  { key: "commission", label: "Comissão de vendas", today: money(r.comissaoAtual), future: money(r.comissaoFuturo) },
  { key: "opex", label: "Despesas operacionais", today: money(r.opexAtual), future: money(r.opexFuturo) },
  { key: "margin", label: "Margem líquida", today: money(r.margemAtual), future: money(r.margemFuturo), bold: true },
];

export const rateRows = (params, advanced, eligible) => {
  const cbs = Number(params.cbsPlena2033) || 0;
  const ibs = Number(params.ibsSpPlena2033) || 0;
  const pct = (v, digits) => `${v.toLocaleString("pt-BR", { minimumFractionDigits: digits, maximumFractionDigits: 2 })}%`;
  const rows = [
    { key: "cbs", label: "CBS", note: "Federal, substitui PIS+COFINS", value: pct(cbs, 1), source: "param" },
    { key: "ibs", label: "IBS", note: "Estadual e municipal, SP", value: pct(ibs, 1), source: "param" },
    { key: "total", label: "CBS + IBS", note: "Alíquota total combinada", value: pct(cbs + ibs, 2), source: "calc", strong: true },
    { key: "iss", label: "ISS", note: "Da aba Parâmetros", value: pct(Number(params.issAtualServicos) || 0, 1), source: "param" },
  ];
  if (advanced) return rows;
  return [
    ...rows,
    { key: "icms", label: "ICMS-SP mercadorias", note: "Peças. Ajustar conforme o NCM", value: "18,00%", source: "fixed" },
    { key: "pis", label: "PIS + COFINS", note: "Peças, total", value: "9,25%", source: "fixed", strong: true },
    { key: "elig", label: "Despesas elegíveis a CBS/IBS", note: eligible === undefined ? "Estimativa conservadora" : "Da matriz do Cadastro", value: eligible === undefined ? "80,00%" : `${(eligible * 100).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%`, source: eligible === undefined ? "fixed" : "sped" },
  ];
};

const pctText = (v) => `${(Number(v) || 0).toLocaleString("pt-BR", { maximumFractionDigits: 2 })}%`;

export const PERCENT_BASE = "o campo em reais e o controle acompanham o percentual, e vice-versa";

// ── Percentual e valor em reais ──────────────────────────────────────────────

const BASE_ON_COST = { pctFrete: "custo", pctMateriais: "custoTotal" };

const baseOf = (field, inputs, price) => {
  const custo = Number(inputs.custo) || 0;
  if (BASE_ON_COST[field] === "custo") return custo;
  if (BASE_ON_COST[field] === "custoTotal") return custo * (1 + fraction(inputs.pctFrete));
  return Number(price) || 0;
};

export const moneyOfPercent = (field, inputs, price) => baseOf(field, inputs, price) * fraction(inputs[field]);

const RESULT_OF = {
  future: { pv: "pvFuturo", pctComissao: "comissaoFuturo", pctOpex: "opexFuturo", pctMargem: "margemFuturo" },
  today: { pv: "pvAtual", pctComissao: "comissaoAtual", pctOpex: "opexAtual", pctMargem: "margemAtual" },
};

/**
 * Valor em reais → percentual. Frete e materiais incidem sobre o custo, então a conta é
 * direta. Comissão, despesas e margem incidem sobre o preço. Se ele está fixo, também é
 * direta. Se o preço é o resultado da conta (`solve`: regime "future" ou "today"), ele muda
 * junto com o percentual, e por isso a busca passa pelo próprio motor de cálculo.
 */
export function percentOfMoney({
  field, value, segmentId, inputs, params, base, solve,
}) {
  const target = Math.max(0, Number(value) || 0);
  if (BASE_ON_COST[field] || !solve) {
    const b = baseOf(field, inputs, base);
    return b > 0 ? (target / b) * 100 : 0;
  }
  let lo = 0;
  let hi = 95;
  for (let i = 0; i < 50; i += 1) {
    const mid = (lo + hi) / 2;
    const r = calcFormacaoPreco(segmentId, toCalcInputs({ ...inputs, [field]: mid }), params);
    const keys = RESULT_OF[solve];
    const got = r[keys.pv] ? r[keys[field]] : Infinity;
    if (got < target) lo = mid; else hi = mid;
  }
  return (lo + hi) / 2;
}

// ── Curva preço × margem ─────────────────────────────────────────────────────

// Marcas redondas no eixo, sempre com o zero entre elas
const yScale = (min, max) => {
  const step = [1, 2, 5, 10, 20, 50].find((s) => (max - min) / s <= 8) || 100;
  const minY = Math.floor(min / step) * step;
  const maxY = Math.ceil(max / step) * step;
  const yTicks = [];
  for (let v = minY; v <= maxY + 1e-9; v += step) yTicks.push(Math.round(v * 100) / 100);
  return { minY, maxY, yTicks };
};

export function buildCurve(segmentId, inputs, params, tested) {
  const calcInputs = toCalcInputs(inputs);
  const atTarget = calcFormacaoPreco(segmentId, calcInputs, params);
  const atZero = calcFormacaoPreco(segmentId, { ...calcInputs, pctMargem: 0 }, params);
  if (!atTarget.pvAtual || !atTarget.pvFuturo || !atZero.pvAtual || !atZero.pvFuturo || !(tested > 0)) return null;

  // Foco na região entre o equilíbrio e o preço mínimo, em qualquer ordem de grandeza. A
  // faixa não depende do preço em teste, para o gráfico não andar enquanto se arrasta a
  // alça; ela só abre se a pessoa digitar um preço fora dela.
  const low = Math.min(atZero.pvAtual, atZero.pvFuturo, atTarget.pvAtual, atTarget.pvFuturo);
  const high = Math.max(atZero.pvAtual, atZero.pvFuturo, atTarget.pvAtual, atTarget.pvFuturo);
  const pad = Math.max((high - low) * 0.55, high * 0.01);
  const minPrice = Math.min(Math.max(low - pad, low * 0.5), tested);
  const maxPrice = Math.max(high + pad, tested);
  const margins = (price) => {
    const m = calcMargemPorPreco(segmentId, calcInputs, params, price);
    return { price, today: m.margemAtual * 100, future: m.margemFutura * 100 };
  };
  const points = Array.from({ length: 81 }, (_, i) => margins(minPrice + ((maxPrice - minPrice) * i) / 80));
  const values = points.flatMap((p) => [p.today, p.future]);
  const at = margins(tested);

  return {
    points,
    minPrice,
    maxPrice,
    ...yScale(Math.min(...values, 0), Math.max(...values, fraction(inputs.pctMargem) * 100)),
    minToday: atTarget.pvAtual,
    minFuture: atTarget.pvFuturo,
    breakToday: atZero.pvAtual,
    breakFuture: atZero.pvFuturo,
    atToday: at.today,
    atFuture: at.future,
    keepTodayPrice: margins(atTarget.pvAtual).future,
  };
}

// ── Cascata do preço ─────────────────────────────────────────────────────────

export function buildCascade(segmentId, inputs, params, r, openTax) {
  if (!r?.pvAtual || !r?.pvFuturo) return { rows: [], lo: 0, hi: 1 };
  const costLabel = "Custo total (com frete)";
  const steps = [];
  if (openTax) {
    const tax = calcImpostoAberto(segmentId, toCalcInputs(inputs), params, r.pvAtual, r.pvFuturo);
    steps.push(
      { key: "debit", label: "Débito de imposto", color: IOS.red, today: -tax.atual.debito, future: -tax.futuro.debito },
      { key: "creditCost", label: "Crédito sobre o custo", color: IOS.green, today: tax.atual.creditoCusto, future: tax.futuro.creditoCusto },
      { key: "creditOpex", label: "Crédito sobre despesas", color: IOS.mint, today: tax.atual.creditoDespesas, future: tax.futuro.creditoDespesas },
    );
  } else {
    steps.push({ key: "tax", label: "Imposto líquido", color: IOS.red, today: -(r.cargaLiquidaAtual || 0) * r.pvAtual, future: -(r.cargaLiquidaFuturo || 0) * r.pvFuturo });
  }
  steps.push(
    { key: "commission", label: "Comissão de vendas", color: IOS.blue, today: -(r.comissaoAtual || 0), future: -(r.comissaoFuturo || 0) },
    { key: "opex", label: "Despesas operacionais", color: IOS.purple, today: -(r.opexAtual || 0), future: -(r.opexFuturo || 0) },
  );

  const grossToday = r.pvAtual - (r.custoTotal || 0);
  const grossFuture = r.pvFuturo - (r.custoTotal || 0);
  let runToday = grossToday;
  let runFuture = grossFuture;
  const middle = steps.map((s) => {
    const row = {
      ...s,
      today: { value: s.today, from: runToday, to: runToday + s.today },
      future: { value: s.future, from: runFuture, to: runFuture + s.future },
    };
    runToday += s.today;
    runFuture += s.future;
    return row;
  });

  const rows = [
    { key: "pv", label: "Preço de venda", total: true, noBar: true, today: { value: r.pvAtual }, future: { value: r.pvFuturo } },
    { key: "cost", label: costLabel, noBar: true, today: { value: -(r.custoTotal || 0) }, future: { value: -(r.custoTotal || 0) } },
    { key: "gross", label: "Sobra depois do custo", color: IOS.gray, total: true, today: { value: grossToday, from: 0, to: grossToday }, future: { value: grossFuture, from: 0, to: grossFuture } },
    ...middle,
    { key: "margin", label: "Margem líquida", color: IOS.green, total: true, today: { value: runToday, from: 0, to: runToday }, future: { value: runFuture, from: 0, to: runFuture } },
  ];
  const edges = rows.filter((row) => !row.noBar).flatMap((row) => [row.today.from, row.today.to, row.future.from, row.future.to]);
  return { rows, lo: Math.min(0, ...edges), hi: Math.max(0, ...edges) };
}

// ── A conta do preço, ano a ano ──────────────────────────────────────────────

export function buildYearCascade(segmentId, inputs, params, price) {
  if (!(price > 0)) return null;
  const calcInputs = toCalcInputs(inputs);
  const cost = calcInputs.custo * (1 + (calcInputs.pctFrete || 0));
  const gross = price - cost;
  const shape = (r) => ({
    gross,
    oldNet: -r.vigente,
    newDebit: -r.debitoNovo,
    creditCost: r.creditoCusto,
    creditOpex: r.creditoDespesas,
    commission: -r.comissao,
    opex: -r.despesas,
    margin: r.margem,
    marginPct: (r.margem / price) * 100,
    taxRate: (r.impostoLiquido / price) * 100,
  });
  const years = calcPrecoPorAno(segmentId, calcInputs, params, price).map((r) => ({ ano: r.ano, fase: r.fase, ...shape(r) }));

  // Hoje: só os tributos atuais, sem CBS nem IBS
  const m = calcMargemPorPreco(segmentId, calcInputs, params, price).margemAtual;
  const commission = (calcInputs.pctComissao || 0) * price;
  const opex = (calcInputs.pctOpex || 0) * price;
  const margin = m * price;
  const vigente = gross - commission - opex - margin;
  const today = {
    gross, oldNet: -vigente, newDebit: 0, creditCost: 0, creditOpex: 0, commission: -commission, opex: -opex, margin, marginPct: m * 100, taxRate: (vigente / price) * 100,
  };

  return {
    price, cost, years, today, multiplier: cost ? price / cost : 0, divisor: price ? cost / price : 0,
  };
}

// ── Alavancas ────────────────────────────────────────────────────────────────

const LEVERS = [
  { key: "custo", label: "Negociar o custo", limit: 0, format: (v) => money(v) },
  { key: "pctFrete", label: "Reduzir o frete", limit: 0, format: pctText },
  { key: "pctComissao", label: "Reduzir a comissão", limit: 0, format: pctText },
  { key: "pctOpex", label: "Cortar despesas", limit: 0, format: pctText },
  { key: "pctMateriais", label: "Mais materiais no serviço", limit: 100, format: pctText, serviceOnly: true },
];

export function buildLevers(segmentId, inputs, params, price) {
  const target = fraction(inputs.pctMargem);
  const marginWith = (over) => calcMargemPorPreco(segmentId, toCalcInputs({ ...inputs, ...over }), params, price).margemFutura;
  const current = marginWith({});
  if (current === null) return null;
  const summary = { target: target * 100, current: current * 100, reached: current >= target - 1e-9 };
  if (summary.reached) return { ...summary, levers: [] };

  const rows = LEVERS.filter((l) => !l.serviceOnly || segmentId === "oficina").map((l) => {
    const now = Number(inputs[l.key]) || 0;
    if (now === l.limit) return null;
    const feasible = marginWith({ [l.key]: l.limit }) >= target;
    if (!feasible) return { key: l.key, label: l.label, feasible: false, currentText: l.format(now) };
    let lo = Math.min(now, l.limit);
    let hi = Math.max(now, l.limit);
    for (let i = 0; i < 50; i += 1) {
      const mid = (lo + hi) / 2;
      const ok = marginWith({ [l.key]: mid }) >= target;
      if (l.limit < now) { if (ok) lo = mid; else hi = mid; } else if (ok) hi = mid; else lo = mid;
    }
    const needed = l.limit < now ? lo : hi;
    const effort = (Math.abs(needed - now) / Math.abs(l.limit - now)) * 100;
    return {
      key: l.key,
      label: l.label,
      feasible: true,
      needed,
      effort,
      currentText: l.format(now),
      neededText: l.format(needed),
      changeText: l.key === "custo"
        ? `−${Math.abs((needed / now - 1) * 100).toLocaleString("pt-BR", { maximumFractionDigits: 1 })}%`
        : `${needed > now ? "+" : "−"}${Math.abs(needed - now).toLocaleString("pt-BR", { maximumFractionDigits: 1 })} p.p.`,
    };
  }).filter(Boolean);

  // O preço sempre resolve: é o mínimo do regime futuro
  const pv = calcFormacaoPreco(segmentId, toCalcInputs(inputs), params).pvFuturo;
  if (pv) {
    const change = (pv / price - 1) * 100;
    rows.push({
      key: "price",
      label: "Subir o preço",
      feasible: true,
      needed: pv,
      effort: Math.min(100, Math.abs(change)),
      currentText: money(price),
      neededText: money(pv),
      changeText: `+${change.toLocaleString("pt-BR", { maximumFractionDigits: 1 })}%`,
    });
  }
  rows.sort((a, b) => Number(b.feasible) - Number(a.feasible) || (a.effort || 0) - (b.effort || 0));
  return { ...summary, levers: rows };
}

// ── A conta, com os números digitados ────────────────────────────────────────

export function buildFormulas(segmentId, inputs, params, r) {
  const cbs = Number(params.cbsPlena2033) || 0;
  const ibs = Number(params.ibsSpPlena2033) || 0;
  const tax = pctText(cbs + ibs);
  const ct = money(r?.custoTotal);
  const c = pctText(inputs.pctComissao);
  const o = pctText(inputs.pctOpex);
  const ml = pctText(inputs.pctMargem);
  const elig = pctText(inputs.despesasElegiveis === undefined ? 80 : inputs.despesasElegiveis * 100);
  const future = segmentId === "oficina"
    ? `${ct} × (1 − ${tax} × ${pctText(inputs.pctMateriais)}) ÷ (1 − ${tax} × (1 − ${elig} × ${o}) − ${c} − ${o} − ${ml})`
    : `${ct} × (1 − ${tax}) ÷ (1 − ${tax} × (1 − ${elig} × ${o}) − ${c} − ${o} − ${ml})`;
  let today;
  if (segmentId === "oficina") today = `${ct} ÷ (1 − ${pctText(params.issAtualServicos)} − ${c} − ${o} − ${ml})`;
  else if (segmentId === "veiculos") today = `${ct} × (1 − 0%) ÷ (1 − 0% − ${c} − ${o} − ${ml})`;
  else today = `${ct} × (1 − 27,25%) ÷ (1 − 27,25% − ${c} − ${o} − ${ml})`;
  return [
    { key: "today", label: "Preço mínimo hoje", result: money(r?.pvAtual), text: today },
    { key: "future", label: "Preço mínimo com CBS/IBS", result: money(r?.pvFuturo), text: future },
  ];
}

